import type { Preferences } from '@cobliteam/common-types';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import AlertDialog, {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@cobliteam/ui/components/AlertDialog';
import Button from '@cobliteam/ui/components/Button';
import Table, { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@cobliteam/ui/components/Table';
import Typography from '@cobliteam/ui/components/Typography';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Input from '@cobliteam/ui/form/Input';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useMemo, useState } from 'react';
import DevToolsPanel from '../DevToolsPanel';

/** Shared by `useLocalStorage` and the reset below, which must clear the same key. */
const PREFERENCES_STORAGE_KEY = 'preferences';

/**
 * Known keys of the `Preferences` shape. This list is maintained by hand and can
 * drift from the type; any extra key found in localStorage is merged in below so
 * unknown values are still visible and removable.
 */
const PREFERENCE_KEYS: Array<keyof Preferences> = [
  'displayLicensePlate',
  'displayVehiclesCluster',
  'displayServiceCard',
  'displayMonitoringGeofences',
  'displayMonitoringGeofencesForceVisibility',
  'displayTrackingGeofences',
  'isOpenSidebar',
  'idleEngineReportThreshold',
  'reportsMailList',
  'geofenceSuccessModal',
  'geofenceTourSeen',
  'hideGeofenceFormatBanner',
  'useMapMatch',
  'isFirstTriggersAccess',
  'hideScheduledMaintenanceBanner',
  'hideVehicleGroupsPreferencesBanner',
  'hideTriggerEventsInfoBanner',
  'hideNewTriggersFeatureInfoBanner',
  'hideIdleEngineOnCalibrationBanner',
  'showSignalTour',
  'hasAgreedNominationNotification',
  'showUserFeedbackTypeform',
  'hasCompletedTripImageHistoryTutorial',
  'showIncidentBanner',
  'openSiteVisibility',
  'disableAnsweredUpsellResearchDialog',
  'answeredUpsellResearchDialog',
  'shownTwoTimesThisWeek',
  'upsellResearchDialogLastShownDate',
  'hideCoblicamPLGIdleEngine',
  'hideCoblicamPLGFleetSafety',
  'safetyRankingTypePage',
  'vehicleGroupIds',
  'enableVehicleDriverAllowList',
  'mediaEventLiveUpdate',
  'typeformSurvey',
  'hideDriverAssociationPastConfirmation',
  'appJoyride',
];

type PreferenceRecord = Record<string, unknown>;

const describeValue = (value: unknown): string => {
  if (value === undefined) return 'Não definida';
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value !== null) return Array.isArray(value) ? `Array(${value.length})` : 'Object';
  return String(value);
};

/** Cycle: not set → true → false → removed. Non-booleans go straight to removed. */
const getCycleAction = (value: unknown) => {
  if (value === undefined) return { icon: <Icon name='circle-slash-2' size='sm' />, label: 'Não definida' };
  if (value === true) return { icon: <Icon name='check' size='sm' className='text-success' />, label: 'true' };
  if (value === false) return { icon: <Icon name='x' size='sm' className='text-danger' />, label: 'false' };
  return { icon: <Icon name='trash-2' size='sm' className='text-danger' />, label: 'Remover' };
};

const PreferencesData = () => {
  const [preferences, setPreferences] = useLocalStorage<Preferences>(PREFERENCES_STORAGE_KEY);
  const [search, setSearch] = useState('');
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  // No mirror state and no sync effect: the localStorage hook is the source of
  // truth, so a render is enough to reflect a change.
  // `Preferences` has no index signature, so a widening hop through `unknown` is
  // required to iterate it generically.
  const current = (preferences ?? {}) as unknown as PreferenceRecord;

  const allKeys = useMemo(() => Array.from(new Set<string>([...PREFERENCE_KEYS, ...Object.keys(current)])).sort(), [current]);

  const missingCount = allKeys.filter((key) => current[key] === undefined).length;

  const visibleKeys = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allKeys.filter((key) => {
      if (term && !key.toLowerCase().includes(term)) return false;
      if (showMissingOnly && current[key] !== undefined) return false;
      return true;
    });
  }, [allKeys, current, search, showMissingOnly]);

  const handleCycle = (key: string, value: unknown) => {
    const next: PreferenceRecord = { ...current };

    if (value === undefined) next[key] = true;
    else if (value === true) next[key] = false;
    else delete next[key];

    setPreferences(next as unknown as Preferences);
  };

  const handleReset = () => {
    localStorage.removeItem(PREFERENCES_STORAGE_KEY);
    window.location.reload();
  };

  if (!preferences) {
    return (
      <DevToolsPanel title='Preferências'>
        <div className='flex flex-col items-start gap-2 rounded-lg border border-gray-6 p-4'>
          <Typography variant='body'>Nenhuma preferência salva neste navegador.</Typography>
          <Typography variant='body' color='secondary'>
            Preferências são criadas conforme você usa o painel. Inicialize um objeto vazio para editá-las manualmente.
          </Typography>
          <Button variant='outlined' color='primary' size='sm' onClick={() => setPreferences({} as Preferences)}>
            Inicializar preferências
          </Button>
        </div>
      </DevToolsPanel>
    );
  }

  return (
    <DevToolsPanel
      title='Preferências'
      description='Clique no valor para alternar: não definida → true → false → removida.'
      actions={
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant='outlined' color='danger' size='sm'>
              Resetar todas
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Resetar todas as preferências?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription>Remove a chave `preferences` do localStorage e recarrega a página. Não é possível desfazer.</AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction color='danger' onClick={handleReset}>
                Resetar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      }
      toolbar={
        <>
          <Input
            size='sm'
            aria-label='Filtrar chaves de preferência'
            placeholder='Filtrar chaves...'
            startIcon={<Icon name='search' size='sm' />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className='flex shrink-0 items-center gap-2'>
            <Checkbox id='show-missing-only' checked={showMissingOnly} onCheckedChange={() => setShowMissingOnly((only) => !only)} />
            <Label htmlFor='show-missing-only' className='whitespace-nowrap'>
              Apenas não definidas ({missingCount})
            </Label>
          </div>
        </>
      }
    >
      {visibleKeys.length === 0 ? (
        <Typography variant='body' color='secondary'>
          Nenhuma chave corresponde aos filtros.
        </Typography>
      ) : (
        <div className='overflow-hidden rounded-lg border border-gray-6'>
          {/* `table-fixed`: with auto layout a single long preference value grew the
              Valor column to 892px, pushing the table to 1356px inside an 842px
              container — so `overflow-hidden` silently clipped the Alternar column
              (and its buttons) out of view. Widths go through TableHead's `styles`
              because the component hardcodes an inline `width: auto` that beats
              any className. */}
          <Table className='table-fixed'>
            <TableHeader>
              <TableRow>
                <TableHead styles={{ width: '45%' }}>Chave</TableHead>
                <TableHead styles={{ width: '31%' }}>Valor</TableHead>
                <TableHead styles={{ width: '24%' }}>Alternar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleKeys.map((key) => {
                const value = current[key];
                const { icon, label } = getCycleAction(value);
                const displayValue = describeValue(value);

                return (
                  <TableRow key={key}>
                    <TableCell className='truncate' title={key}>
                      <Typography variant='caption-xs' className='font-mono'>
                        {key}
                      </Typography>
                    </TableCell>
                    <TableCell className='truncate' title={displayValue}>
                      <Typography variant='caption-xs' color={value === undefined ? 'secondary' : 'default'}>
                        {displayValue}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant='outlined'
                        color='default'
                        size='sm'
                        startIcon={icon}
                        className='w-32 justify-start'
                        onClick={() => handleCycle(key, value)}
                        aria-label={`Alternar ${key}`}
                      >
                        {label}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </DevToolsPanel>
  );
};

export default PreferencesData;
